// Base
import { Controller } from "react-hook-form";
import parse from "html-react-parser";
import { useState, useEffect } from "react";

// Types
import type { Ref, FC } from "react";
import type {
  Control,
  FieldErrors,
  UseFormRegister,
  UseFormSetValue,
} from "react-hook-form";
import type { CheckoutFormValues } from "@/types/types";
import type { ApplePayData, GooglePayData } from "@/types/types";
import type { AxiosRequestConfig } from "axios";

// Components
import Accordion from "@/components/Accordion";
import CardField from "@/components/Inputs/CardField";
import Expiration from "@/components/Inputs/Expiration";
import CardCvc from "@/components/Inputs/CardCvc";
import GooglePayButton from "@/components/Buttons/GooglePayButton";
import PaypalButton from "@/components/Buttons/PaypalButton";
import ApplePayButton from "@/components/Buttons/ApplePayButton";
import CardButton from "@/components/Buttons/CardButton";

// Store
import { useTemplateStore } from "@/zustand/templateStore";

// Assets
import PaymentsIcon from "@/assets/paymentColor.svg";
import paypalIcon from "@/assets/paypal-new.svg";
import applePayIcon from "@/assets/apple-pay.svg";
import googlePayIcon from "@/assets/google-pay-mark_800.svg";
import Browser from "@/assets/browser.svg";
import SecureLogos from "@/assets/secure-payment-logos.svg";
import SecureLogosContrast from "@/assets/secure-payment-logos-contrast.svg";
import { Lock } from "@/assets/Icons";

interface PaymentMethodProps {
  form: Record<string, string | undefined>;
  paymentMethod: "card" | "paypal" | "applepay" | "googlepay" | string;
  expirationRef?: Ref<HTMLDivElement>;
  control: Control<any>;
  errors: FieldErrors<any>;
  register: UseFormRegister<any>;
  setValue: UseFormSetValue<any>;
  values: CheckoutFormValues;
  submitCount: number;
  handlePaypalSubmit: () => void;
  handleApplePaySubmit(
    applePayData: ApplePayData,
    options?: AxiosRequestConfig,
  ): Promise<void>;
  handleGooglePaySubmit(
    googlePayData: GooglePayData,
    options?: AxiosRequestConfig,
  ): Promise<void>;
  formSubmited?: boolean;
  activePaypal: boolean;
  activeApplePay: boolean;
  activeGooglePay: boolean;
}

interface ExpressMethod {
  label: string;
  name: string;
  icon: string;
  text?: string;
  isAvailable?: boolean;
}

const PaymentMethod: FC<PaymentMethodProps> = ({
  form,
  paymentMethod,
  expirationRef,
  formSubmited,
  control,
  errors,
  register,
  setValue,
  handlePaypalSubmit,
  handleApplePaySubmit,
  handleGooglePaySubmit,
  activePaypal,
  activeApplePay,
  activeGooglePay,
}) => {
  const { settings, isApplePayAvailable, isGooglePayAvailable } =
    useTemplateStore();
  const [expressMethods, setExpressMethods] = useState<ExpressMethod[]>([]);

  useEffect(() => {
    setExpressMethods([
      {
        isAvailable: activePaypal,
        label: "PayPal",
        name: "paypal",
        icon: paypalIcon,
      },
      {
        isAvailable: activeApplePay,
        label: "Apple Pay",
        name: "applepay",
        icon: applePayIcon,
      },
      {
        isAvailable: activeGooglePay,
        label: "Google Pay",
        name: "googlepay",
        icon: googlePayIcon,
      },
    ]);
  }, [
    isApplePayAvailable,
    isGooglePayAvailable,
    activeGooglePay,
    activePaypal,
    activeApplePay,
  ]);

  return (
    <div className="w-full">
      <h1
        className={`mb-4 text-xl font-bold ${
          settings.enable_high_contrast ? "text-cc-dark-gray" : "text-cc-gray"
        }`}
      >
        {form?.tr_step_3_title}
      </h1>

      <div ref={expirationRef} className="">
        <Accordion
          contrast={settings.enable_high_contrast}
          label={form?.tr_card_label}
          image={PaymentsIcon}
          name="card"
          isFirst
        >
          <div className="flex w-full flex-col gap-3">
            <Controller
              control={control}
              name="card_number"
              render={({ field: { value } }) => (
                <CardField
                  type="tel"
                  name="card_number"
                  placeholder={form?.tr_card_label}
                  error={errors.card_number?.message as string | undefined}
                  value={value}
                  register={register}
                  setValue={setValue}
                  contrast={settings.enable_high_contrast}
                />
              )}
            />
            <div className="flex flex-col gap-3 md:flex-row">
              <Controller
                control={control}
                name="expiration"
                render={({ field: { onChange, value } }) => (
                  <Expiration
                    name="expiration"
                    value={value}
                    handleChange={onChange}
                    placeholder={form?.tr_expiration_label}
                    error={
                      (errors.card_exp_year?.message ||
                        errors.card_exp_month?.message) as string | undefined
                    }
                    contrast={settings.enable_high_contrast}
                  />
                )}
              />

              <Controller
                control={control}
                name="card_cvc"
                render={({ field: { onChange, value } }) => (
                  <CardCvc
                    name="card_cvc"
                    register={register}
                    setValue={setValue}
                    handleChange={onChange}
                    value={value}
                    placeholder={
                      form?.tr_security_code_label ?? "Security code"
                    }
                    error={errors.card_cvc?.message as string | undefined}
                    max={4}
                    contrast={settings.enable_high_contrast}
                    min={3}
                    className="w-full md:w-1/2"
                  />
                )}
              />
            </div>
          </div>
        </Accordion>

        {expressMethods
          .filter((method) => method.isAvailable)
          .map(({ label, name, icon }, index, arr) => (
            <Accordion
              contrast={settings.enable_high_contrast}
              key={name}
              label={label}
              image={icon}
              name={name}
              isLast={index === arr.length - 1}
            >
              <div className="flex flex-col items-center">
                <img src={Browser} alt="Browser" className="mb-4 max-w-16" />
                <p
                  className={`text-center text-sm ${
                    settings.enable_high_contrast
                      ? "text-cc-dark-gray"
                      : "text-cc-gray"
                  }`}
                >
                  {form?.tr_express_checkout_description?.replace(
                    "###payment_type###",
                    label,
                  )}
                </p>
              </div>
            </Accordion>
          ))}

        <div className="mt-6 flex flex-col space-y-2">
          {paymentMethod === "paypal" && (
            <PaypalButton
              handlePaypalSubmit={handlePaypalSubmit}
              className="bg-cc-yellow-2 border-cc-orange w-full cursor-pointer rounded-md border-b-2 px-6 py-3"
            />
          )}
          {paymentMethod === "card" && (
            <CardButton
              processingLabel={form?.tr_processing_label}
              loading={formSubmited}
            />
          )}

          {paymentMethod === "applepay" && (
            <ApplePayButton
              height={48}
              className="w-full cursor-pointer rounded-md bg-black px-6 py-[18px]"
              onApplePaySubmit={handleApplePaySubmit}
            />
          )}
          {paymentMethod === "googlepay" && (
            <GooglePayButton
              className="w-full cursor-pointer rounded-md bg-black px-6 py-[18px]"
              height={48}
              onGooglePaySubmit={handleGooglePaySubmit}
            />
          )}
        </div>
        <div className="mt-4 flex justify-center">
          <img
            src={
              settings.enable_high_contrast ? SecureLogosContrast : SecureLogos
            }
            alt="Secure Payment Logos"
          />
        </div>
        <div className="bg-cc-gray-4 my-4 h-px w-full" />
        <div
          className={`flex items-center justify-center gap-2 text-sm ${
            settings.enable_high_contrast
              ? "text-cc-dark-gray"
              : "text-cc-gray-3"
          }`}
        >
          <Lock />
          <span>{parse(form?.tr_secure_payment_label || "")}</span>
        </div>
        <div
          className={`text-center text-sm ${
            settings.enable_high_contrast
              ? "text-cc-dark-gray"
              : "text-cc-gray-3"
          }`}
        >
          {form?.tr_secondary_secure_payment_label}
        </div>
      </div>
    </div>
  );
};

export default PaymentMethod;
